/**
 * Счётчик подарочных дней — шапка сайта и личный кабинет.
 *
 * Кого как показываем:
 *   последний день подарка (gift_last_day) — «Последний день ЛАЙТ в
 *     подарок — сгорает сегодня» и кнопка «Оформить ПРО»;
 *   подарок за регистрацию (tier_source === 'trial', срок впереди) — это
 *     ЛАЙТ, не ПРО: «Осталось 9 дней ЛАЙТ в подарок — каждый день сгорает»;
 *     при 3 днях и меньше — сильнее (urgent). Рядом — ссылка на одну новую
 *     программу ПРО, по очереди по дню (spotlight, constants/programs.js
 *     PRO_SPOTLIGHT): «В ПРО — Любовь по твоей карте →»;
 *   оплаченный ПРО со сроком — спокойно: «ПРО до 22.11.2026»;
 *   ПРО навсегда — «ПРО навсегда»;
 *   гость, человек без ПРО, админ — ничего (null).
 *
 * Число дней своей арифметики здесь не имеет: его считает сервер одним
 * правилом (backend/gift_days.py) и отдаёт в /auth/me полем gift_days_left —
 * то же число берут блок «Программы» в кабинете и строка в дневном письме.
 * В день регистрации 14, каждый день по МСК на 1 меньше, в последний день 1,
 * дальше null — счётчика нет.
 */
import { isProOrLifetime } from '@/constants/tiers';
import { PRO_SPOTLIGHT, programByKey } from '@/constants/programs';
import { formatMskDate } from '@/lib/mskDate';

const DAY_MS = 86400000;

function parse(raw) {
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

function plural(n, one, few, many) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}

export const URGENT_DAYS = 3;
export const LAST_DAY_TEXT = 'Последний день ЛАЙТ в подарок — сгорает сегодня';

/**
 * Подарочные дни ЛАЙТ из ответа сервера (14..1) или null. Единственный
 * источник числа на сайте — поле gift_days_left из /auth/me.
 */
export function giftDaysLeft(user) {
  const n = user ? Number(user.gift_days_left) : NaN;
  return Number.isInteger(n) && n >= 1 && n <= 14 ? n : null;
}

/** ДД.ММ.ГГГГ по МСК — как все даты доступа (lib/mskDate.js). */
export function formatDateDots(d) {
  return formatMskDate(d, '');
}

export function trialLine(days) {
  const left = plural(days, 'Остался', 'Осталось', 'Осталось');
  return `${left} ${days} ${plural(days, 'день', 'дня', 'дней')} ЛАЙТ в подарок — каждый день сгорает`;
}

/** Программа ПРО дня: по очереди, меняется в местную полночь. */
export function spotlightFor(now = Date.now()) {
  const d = new Date(now);
  const dayNo = Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / DAY_MS);
  const item = PRO_SPOTLIGHT[((dayNo % PRO_SPOTLIGHT.length) + PRO_SPOTLIGHT.length) % PRO_SPOTLIGHT.length];
  const program = programByKey(item.key);
  return { key: item.key, text: `В ПРО — ${item.label} →`, path: program ? program.path : '/pricing' };
}

/** Состояние счётчика или null, если показывать нечего. */
export function proDaysState(user, now = Date.now()) {
  if (!user) return null;
  const source = user.tier_source;
  if (source === 'admin') return null;

  if (source === 'trial') {
    // Последний день подарка: счётчика уже нет (вчера было «1»), подарок
    // сгорает сегодня — признак тоже с сервера (gift_last_day, gift_days.py).
    if (user.gift_last_day) {
      return { kind: 'last', urgent: true, text: LAST_DAY_TEXT, cta: 'Оформить ПРО' };
    }
    const days = giftDaysLeft(user);
    if (!days) return null;
    return {
      kind: 'trial', days, urgent: days <= URGENT_DAYS, text: trialLine(days),
      spotlight: spotlightFor(now),
    };
  }

  if (!isProOrLifetime(user)) return null;
  if (user.is_lifetime || source === 'lifetime') {
    return { kind: 'lifetime', text: 'ПРО навсегда' };
  }
  const until = parse(user.tier_expires_at || user.subscription_expires_at);
  if (!until || until.getTime() <= now) return null;
  return { kind: 'paid', until: formatDateDots(until), text: `ПРО до ${formatDateDots(until)}` };
}
